import React, { useState, useRef, useLayoutEffect } from 'react';
import { Search, Clock, BookOpen, MessageCircle, Info } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import articlesMap from '../articles';
import { Link } from 'react-router-dom';
import FilterCheck from '../components/FilterCheck';
import T from '../components/T';
import { Helmet } from 'react-helmet-async';


interface Article {
  id: number;
  title: string;
  excerpt: string;
  date: string;
  category: string;
  type: 'scientific' | 'opinion' | 'informative';
  readingTime: number;
  size: 'small' | 'medium' | 'large';
  link: string;
  image?: string;
  lineWidth?: string | { en: string; nl: string };
}


const Articles: React.FC = () => {
  const { t, language } = useLanguage(); // Use the custom hook
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<"date" | "readingTime">("date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">('desc');
  const [selectedFilters, setSelectedFilters] = useState<('scientific' | 'opinion' | 'informative')[]>(['scientific', 'opinion', 'informative']);

  const toggleFilter = (filterType: 'scientific' | 'opinion' | 'informative') => {
    setSelectedFilters(prev => 
      prev.includes(filterType) 
        ? prev.filter(f => f !== filterType)
        : [...prev, filterType]
    );
  };

  const titleMeasurements = useRef<Record<number, { width: number; left: number; top: number; height: number }>>({});
  const [, forceRerender] = useState(0); // used to trigger rerender when measurements change
  const titleRefs = useRef<Record<number, HTMLSpanElement | null>>({});
  const getTypeIcon = (type: 'scientific' | 'opinion' | 'informative') => {
    switch (type) {
      case 'scientific': return <BookOpen style={{color: 'var(--color-alt-accent)'}} size={20} />;
      case 'opinion': return <MessageCircle style={{color: 'var(--color-main-accent)'}} size={20} />;
      case 'informative': return <Info style={{color: 'var(--color-deep-red)'}} size={20} />;
    }
  };

  const getTypeColor = (type: 'scientific' | 'opinion' | 'informative') => {
    switch (type) {
      case 'scientific': return 'var(--color-alt-accent)';
      case 'opinion': return 'var(--color-main-accent)';
      case 'informative': return 'var(--color-deep-red)';
    }
  };

  // Load articles from the explicit articles index mapping (each entry exposes Component and meta)
  const baseArticles: Article[] = [
    {
      id: 1,
      title: t("articlePage.articles.LLMDSL.title"),
      excerpt: t("articlePage.articles.LLMDSL.excerpt"),
      date: "2024-09-01",
      category: t("articlePage.articles.category.science"),
      type: "scientific",
      readingTime: 20,
      size: "medium",
      link: "https://pubs.rsc.org/en/Content/ArticleLanding/2024/DD/D4DD00366G",
      image: "./images/developing_llms.png",
      lineWidth: {
        en: "18%",
        nl: "38%"
      }
    },
    {
      id: 2,
      title: t("articlePage.articles.alLabelling.title"),
      excerpt: t("articlePage.articles.alLabelling.excerpt"),
      date: "2021-10-03",
      category: t("articlePage.articles.category.science"),
      type: "scientific",
      readingTime: 30,
      size: "medium",
      link: "https://arxiv.org/abs/2109.04847",
      lineWidth: {
        en: "83%",
        nl: "25%"
      }
    }
  ];

  const DISCOVER_OFFSET = 10000;
  const discovered = Object.keys(articlesMap).map((key, idx) => {
    const entry = articlesMap[key] as { Component: React.FC; meta?: any };
    const meta = entry.meta || {};
    const idStr = meta.id ?? key;
    const idNum = DISCOVER_OFFSET + idx;
    return {
      id: idNum,
      title: meta.title || 'Untitled',
      excerpt: meta.excerpt || '',
      date: meta.date || '1970-01-01',
      category: meta.category || '',
      type: (meta.type as any) || 'informative',
      readingTime: meta.readingTime || 1,
      size: (meta.size as any) || 'medium',
      link: meta.externalLink ? meta.externalLink : `/article/${idStr}`,
      image: meta.image,
      lineWidth: meta.lineWidth,
    } as Article;
  });

  const articles: Article[] = [...baseArticles, ...discovered];

  
  const filteredAndSortedArticles = articles
    .filter(article => selectedFilters.includes(article.type))
    .filter(article =>
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === "date") {
        const diff = new Date(a.date).getTime() - new Date(b.date).getTime();
        return sortDirection === 'asc' ? diff : -diff;
      } else {
        const diff = a.readingTime - b.readingTime;
        return sortDirection === 'asc' ? diff : -diff;
      }
    });

  // Measure the last visual line rect for each title and store measurements relative to the title container
  useLayoutEffect(() => {
    const measureAll = () => {
      let anyChanged = false;
      filteredAndSortedArticles.forEach(article => {
        const el = titleRefs.current[article.id];
        if (!el) return;

        const range = document.createRange();
        range.selectNodeContents(el);
        const rects = Array.from(range.getClientRects());
        if (rects.length === 0) return;
        const lastRect = rects[rects.length - 1];

        const containerRect = el.getBoundingClientRect();
        const relLeft = Math.round(lastRect.right - containerRect.left);
        const relTop = Math.round(lastRect.top - containerRect.top);
        const relWidth = Math.round(lastRect.width);
        const relHeight = Math.round(lastRect.height);

        const prev = titleMeasurements.current[article.id];
        if (!prev || prev.width !== relWidth || prev.left !== relLeft || prev.top !== relTop || prev.height !== relHeight) {
          titleMeasurements.current[article.id] = { width: relWidth, left: relLeft, top: relTop, height: relHeight } as any;
          anyChanged = true;
        }
      });
      if (anyChanged) forceRerender(n => n + 1);
    };

    measureAll();

    // Re-measure after fonts load to avoid measurement shifts on refresh
    let remeasureTimer: number | null = null;
    const extraTimers: number[] = [];
    const imgListeners: Array<{ img: HTMLImageElement; fn: () => void }> = [];

    if (document && (document as any).fonts && (document as any).fonts.ready) {
      (document as any).fonts.ready.then(() => {
        measureAll();
      }).catch(() => {});
    }

    // Extra delayed re-measures to catch late layout changes (and slow image loads)
    remeasureTimer = window.setTimeout(() => measureAll(), 200);
    extraTimers.push(window.setTimeout(() => measureAll(), 500));
    extraTimers.push(window.setTimeout(() => measureAll(), 900));

    // Also re-measure when images finish loading (some cards include images that cause layout shifts)
    if (typeof document !== 'undefined' && document.images) {
      Array.from(document.images).forEach((img) => {
        const image = img as HTMLImageElement;
        if (!image.complete) {
          const onImgLoad = () => {
            measureAll();
            image.removeEventListener('load', onImgLoad);
          };
          image.addEventListener('load', onImgLoad);
          imgListeners.push({ img: image, fn: onImgLoad });
        }
      });
    }

    const onResize = () => measureAll();
    const onLoad = () => measureAll();

    window.addEventListener('resize', onResize);
    window.addEventListener('load', onLoad);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('load', onLoad);
      if (remeasureTimer) clearTimeout(remeasureTimer);
      extraTimers.forEach(t => clearTimeout(t));
      imgListeners.forEach(({ img, fn }) => img.removeEventListener('load', fn));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredAndSortedArticles.map(a => a.id).join(','), language, searchTerm, sortBy, selectedFilters]);

  return (
    <div className="bg-[var(--color-bg-primary)] py-[8rem] min-h-screen transition-colors duration-300 overflow-x-hidden w-full" style={{fontFamily: 'Short Stack, cursive'}}>
      <Helmet>
        <title>{t('seo.articles.title')}</title>
        <meta name="description" content={t('seo.articles.description')} />
        <meta property="og:title" content={t('seo.articles.title')} />
        <meta property="og:description" content={t('seo.articles.description')} />
        <meta property="og:image" content="https://pfjacobs.com/images/profile_without_bg.png" />
        <meta property="og:url" content="https://pfjacobs.com/articles" />
      </Helmet>
      <div className="container mx-auto px-6 max-w-6xl transition-colors duration-300">
        <h1 className="text-3xl md:text-4xl lg:text-5xl text-center mb-16 mt-8 text-[var(--color-text-primary)] font-normal" style={{fontFamily: 'Indie Flower, cursive'}}><T k="articlePage.title" /></h1>
        <div className="flex flex-col lg:flex-row justify-between items-stretch lg:items-center mb-8 gap-4 min-w-0">
          <div className="relative flex-1 min-w-0 order-1 lg:order-1">
            <input
              type="text"
              placeholder={t('articlePage.searchPlaceholder')}
              aria-label={t('articlePage.searchLabel')}
              className="w-full pl-10 pr-4 py-2 bg-transparent text-[var(--color-text-primary)] transition-colors duration-300"
              style={{
                border: 'none',
              }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--color-text-muted)]" size={20} />
          </div>

          {/* Sort controls (on mobile, appear below search and above filters).
              One dropdown for both field and direction (not a separate
              field dropdown + direction dropdown/arrow button) — the value
              encodes both as "field-direction" and is split back out on
              change. */}
          <div className="flex items-center space-x-2 order-2 lg:order-3 self-start lg:self-auto">
            <select
              className="sort-select p-2 pr-6 transition-colors duration-300"
              style={{
                border: 'none',
                backgroundColor: 'transparent',
                color: 'var(--color-text-primary)'
              }}
              value={`${sortBy}-${sortDirection}`}
              onChange={(e) => {
                const [field, direction] = e.target.value.split('-') as ["date" | "readingTime", "asc" | "desc"];
                setSortBy(field);
                setSortDirection(direction);
              }}
            >
              <option value="date-desc">{t('articlePage.sort.newest')}</option>
              <option value="date-asc">{t('articlePage.sort.oldest')}</option>
              <option value="readingTime-asc">{t('articlePage.sort.shortest')}</option>
              <option value="readingTime-desc">{t('articlePage.sort.longest')}</option>
            </select>
          </div>

          {/* Filter pills: FilterCheck, styled in input.css (.filter-check). */}
          <div className="flex flex-wrap gap-x-6 gap-y-1 order-3 lg:order-2">
            <FilterCheck label={t('articlePage.filters.scientific')} color="var(--color-alt-accent)" checked={selectedFilters.includes('scientific')} onClick={() => toggleFilter('scientific')} />
            <FilterCheck label={t('articlePage.filters.opinion')} color="var(--color-main-accent)" checked={selectedFilters.includes('opinion')} onClick={() => toggleFilter('opinion')} />
            <FilterCheck label={t('articlePage.filters.informative')} color="var(--color-deep-red)" checked={selectedFilters.includes('informative')} onClick={() => toggleFilter('informative')} />
          </div>
        </div>

        <div className="flex flex-col gap-8">
          {filteredAndSortedArticles.map((article, index) => (
            <div 
              key={article.id} 
              className="w-full transition-all duration-300 relative z-20 rounded-lg overflow-hidden min-w-0"
              style={{
                backgroundColor: 'var(--color-bg-primary)'
              }}
            >
              {/* Small corner category logo — bare icon, no box for now */}
              <div className="absolute top-6 right-0 z-30">
                {getTypeIcon(article.type)}
              </div>

              {/* Article Content - now full width */}
              <div className="w-full py-6 flex flex-col min-w-0">
                {article.link && article.link.startsWith('/') ? (
                  <Link to={article.link} className="group">
                    <h2 
                      className="text-xl md:text-2xl font-medium group-hover:underline mb-3 pr-8 text-[var(--color-text-primary)] transition-colors duration-300"
                      style={{fontFamily: 'Short Stack, cursive'}}
                    >
                      <span style={{ position: 'relative', display: 'block', width: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
                        <span
                          ref={el => { titleRefs.current[article.id] = el; }}
                          style={{ whiteSpace: 'pre-line', display: 'inline', overflowWrap: 'anywhere', wordBreak: 'break-word', maxWidth: '100%' }}
                        >
                          {article.title}
                        </span>
                        {/* external link icon removed intentionally */}
                      </span>
                    </h2>
                  </Link>
                ) : (
                  <a href={article.link} target="_blank" rel="noopener noreferrer" className="group">
                    <h2 
                      className="text-xl md:text-2xl font-medium group-hover:underline mb-3 pr-8 text-[var(--color-text-primary)] transition-colors duration-300"
                      style={{fontFamily: 'Short Stack, cursive'}}
                    >
                      <span style={{ position: 'relative', display: 'block', width: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
                        <span
                          ref={el => { titleRefs.current[article.id] = el; }}
                          style={{ whiteSpace: 'pre-line', display: 'inline', overflowWrap: 'anywhere', wordBreak: 'break-word', maxWidth: '100%' }}
                        >
                          {article.title}
                        </span>
                        {/* external link icon removed intentionally */}
                      </span>
                    </h2>
                  </a>
                )}
                
                {/* Signature colored line below title */}
                <div 
                  className="h-1 rounded-full mb-2 transition-all duration-300"
                  style={{ 
                    backgroundColor: article.type === 'scientific'
                      ? 'var(--color-alt-accent)'
                      : getTypeColor(article.type),
                    width: titleMeasurements.current[article.id]
                      ? `${titleMeasurements.current[article.id].width + 12}px`
                      : ((typeof article.lineWidth === 'object'
                        ? (language === 'nl' ? article.lineWidth.nl : article.lineWidth.en)
                        : article.lineWidth) || '8rem'),
                    transform: titleMeasurements.current[article.id]
                      ? (article.link && !article.link.startsWith('/') ? 'translateY(-8px)' : 'translateY(-6px)')
                      : undefined
                  }}
                ></div>
                <p className="text-base lg:text-lg text-[var(--color-text-secondary)] mb-6 flex-grow transition-colors duration-300">
                  {article.excerpt}
                </p>

                <div className="flex justify-between items-center text-sm text-[var(--color-text-muted)] transition-colors duration-300 mt-auto">
                  <span>{new Date(article.date).toLocaleDateString()}</span>
                  <div className="flex items-center">
                    <Clock size={16} className="mr-1" />
                    <span>{article.readingTime} <T k="articlePage.timeRead" /></span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Articles;